import React from 'react';
import { SubmissionError, Field, reduxForm } from 'redux-form';
import { Grid } from '@material-ui/core';
import moment from 'moment';
import {
  FormTextField,
  FormFormattedField
} from '../../../../components/Form/index';
import {
  OfficePhoneLabel,
  StepControl,
  FormWrapper,
  FieldWrapper,
  AddressComponent,
  StepperTitle
} from '../components';
import { maxLength, required } from '../../../../utils/validators';
import {
  ONBOARDING_STEPS,
  DATE_FORMAT,
  REGISTRATION_MAX_STRING_LENGTH
} from '../../../../constants';
import scrollToError from '../../../../utils/scrollToError';
import { user } from '../../../../utils/api';

const maxStringLength = maxLength(REGISTRATION_MAX_STRING_LENGTH);

const dealerLicenseValidation = value =>
  value && /^[a-zA-Z0-9-]{1,10}$/.test(value)
    ? undefined
    : 'Dealer license must contain from 1 to 10 digits';

const sellerPermitNumberValidation = value =>
  value && /^[a-zA-Z0-9-]{9}$/.test(value)
    ? undefined
    : 'Dealer license must contain 9 digits';

const phoneNumberValidation = value =>
  value && value.replace(/[\D]/g, '').length === 10
    ? undefined
    : 'Phone number must contain 10 digits';
const notRequiredPhoneNumberValidation = value =>
  (value && value.replace(/[\D]/g, '').length === 10) ||
    (value || '').length === 0
    ? undefined
    : 'Phone number must contain 10 digits';

const isValidDate = date => {
  if (!date) {
    return undefined;
  }
  return moment(date, DATE_FORMAT, true).isValid()
    ? undefined
    : 'Dealer license expiry date is invalid';
};

const isExpired = date => {
  if (!date) {
    return undefined;
  }
  return moment().isBefore(moment(date, DATE_FORMAT))
    ? undefined
    : 'Dealer license is already expired';
};

const continueOnboarding = onSubmit => values =>
  user
    .continueOnboarding({
      ...values,
      purchase_method: [],
      phone_number: (values.phone_number || '').replace(/[\D]/g, ''),
      secondary_contact_phone: (values.secondary_contact_phone || '').replace(
        /[\D]/g,
        ''
      ),
      store_number: (values.store_number || '').replace(/[\D]/g, ''),
      last_step: ONBOARDING_STEPS.THIRD_BUYER_STEP
    })
    .then(() => {
      onSubmit();
    })
    .catch(({ response }) => {
      if (response.status === 400) {
        const errors = (response.data.data || []).reduce(
          (acc, error) => ({
            ...acc,
            [error.field]: error.message
          }),
          {}
        );
        // eslint-disable-next-line no-throw-literal
        throw new SubmissionError(errors);
      }
    });

const secondBuyerStep = ({ handleSubmit, prevStep, nextStep }) => (
  <form onSubmit={handleSubmit(continueOnboarding(nextStep))}>
    <FormWrapper>
      <StepperTitle title="Buyer Info" subTitle="Step 2 of 5" />
      <Grid container spacing={24}>
        <AddressComponent />
        <FieldWrapper>
          <Field
            name="phone_number"
            component={FormFormattedField}
            label="Mobile phone number"
            fullWidth
            maskProps={{
              format: '(###) ###-####',
              mask: '_'
            }}
            validate={[required, phoneNumberValidation]}
          />
        </FieldWrapper>
        <FieldWrapper>
          <Field
            name="store_number"
            component={FormFormattedField}
            label="Store phone number (Optional)"
            fullWidth
            maskProps={{
              format: '(###) ###-####',
              mask: '_'
            }}
            validate={notRequiredPhoneNumberValidation}
          />
        </FieldWrapper>
        <FieldWrapper>
          <Field
            name="secondary_contact_name"
            component={FormTextField}
            label="Secondary contact name (Optional)"
            fullWidth
            validate={maxStringLength}
          />
        </FieldWrapper>
        <FieldWrapper>
          <Field
            name="secondary_contact_phone"
            component={FormFormattedField}
            label="Secondary contact phone number (Optional)"
            fullWidth
            maskProps={{
              format: '(###) ###-####',
              mask: '_'
            }}
            validate={notRequiredPhoneNumberValidation}
          />
        </FieldWrapper>
        <FieldWrapper>
          <Field
            name="dealer_license_number"
            component={FormTextField}
            label="DMV Vehicle Dealer License Number"
            fullWidth
            validate={[required, dealerLicenseValidation]}
          />
        </FieldWrapper>
        <FieldWrapper>
          <Field
            name="dealer_license_expiry_date"
            component={FormFormattedField}
            label="DMV Vehicle Dealer License Expiry Date"
            fullWidth
            maskProps={{
              format: '##/##/####',
              placeholder: DATE_FORMAT,
              mask: ['M', 'M', 'D', 'D', 'Y', 'Y', 'Y', 'Y']
            }}
            placeholderOnFocus="MM/DD/YYYY"
            validate={[required, isValidDate, isExpired]}
          />
        </FieldWrapper>
        <FieldWrapper>
          <Field
            name="seller_permit_number"
            component={FormTextField}
            label="Seller Permit Number"
            fullWidth
            validate={[required, sellerPermitNumberValidation]}
          />
        </FieldWrapper>
      </Grid>
    </FormWrapper>
    <OfficePhoneLabel />
    <StepControl prevStep={prevStep} />
  </form>
);

export default reduxForm({
  form: 'registrationForm',
  destroyOnUnmount: false,
  fields: [
    'street',
    'city',
    'state',
    'zip',
    'phone_number',
    'store_number',
    'secondary_contact_name',
    'secondary_contact_phone',
    'dealer_license_number',
    'dealer_license_expiry_date'
  ],
  onSubmitFail: scrollToError
})(secondBuyerStep);
